import { Injectable } from "@angular/core";
import { DataService } from "@app/core/services/data.service";
import { CfsTemplate, TemplateStatusPostDto, TemplateStatusResponseDto, TemplateStatusTemplatePostDto } from "@app/models";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import {
  DisplayTypeDto,
  GeneratedTemplateGetDto,
  nonFalsyPayload,
  SystemTemplatePutDto,
  TemplateAssignedAccountsDto,
  TemplatePreviewPostDto,
  TemplateValidationResultDto
} from "clearline-api";

@Injectable({ providedIn: "root" })
export class TemplateService {
  // todo: reuse methods from clearline-api:
  serviceUrl = "template";

  constructor(private http: DataService) {}

  get(templateId: number): Observable<CfsTemplate> {
    return this.http.get(`${this.serviceUrl}/${templateId}`);
  }

  getAssignedAccounts(templateId: number, isGlobal: boolean): Observable<TemplateAssignedAccountsDto> {
    return this.http.get(`${this.serviceUrl}/${templateId}/assigned/accounts?isGlobal=${isGlobal}`);
  }

  assignAccounts(templateId: number, isGlobal: boolean, data: TemplateAssignedAccountsDto): Observable<void> {
    return this.http.post(`${this.serviceUrl}/${templateId}/assigned/accounts?isGlobal=${isGlobal}`, data);
  }

  getGlobal(templateId: number): Observable<CfsTemplate> {
    return this.http.get(`${this.serviceUrl}/global/${templateId}`);
  }

  getAll(locationId: number): Observable<CfsTemplate[]> {
    return this.http.get(`${this.serviceUrl}/bylocation/${locationId}`);
  }

  getAllByAccount(accountId: number): Observable<CfsTemplate[]> {
    return this.http.get(`${this.serviceUrl}/byaccount/${accountId}`);
  }

  getAllGlobal(): Observable<CfsTemplate[]> {
    return this.http.get(`${this.serviceUrl}/global`);
  }

  getFilteredAccountTemplates(locationId: number): Observable<CfsTemplate[]> {
    return this.http.get(`${this.serviceUrl}/filter/${locationId}`);
  }

  getCfsItems(locationId: number): Observable<CfsTemplate[]> {
    return this.http.get(`${this.serviceUrl}/cfs/${locationId}`);
  }

  getGenerated(templateId: number, locationId: number | null = null, cashRegisterId: number | null = null): Observable<CfsTemplate> {
    const params = nonFalsyPayload({ locationId, cashRegisterId });
    return this.http.get(`${this.serviceUrl}/generated/${templateId}`, params);
  }

  getGeneratedGlobal(templateId: number, locationId: number | null = null, cashRegisterId: number | null = null): Observable<CfsTemplate> {
    const params = nonFalsyPayload({ locationId, cashRegisterId });
    return this.http.get(`${this.serviceUrl}/generated-global/${templateId}`, params);
  }

  getDisplayTypes(): Observable<DisplayTypeDto[]> {
    return this.http.get(`${this.serviceUrl}/displayTypes`);
  }

  getConfigurable(accountId?: number): Observable<CfsTemplate[]> {
    const url = accountId != null ? `${this.serviceUrl}/configurable/${accountId}` : `${this.serviceUrl}/configurable`;
    return this.http.get(url).pipe(
      map((x: CfsTemplate[]) => {
        TemplateService.setTemplatesFullName(x);
        return x;
      })
    );
  }

  getTypes(): Observable<CfsTemplate[]> {
    return this.http.get(`${this.serviceUrl}/types`).pipe(
      map((x: CfsTemplate[]) => {
        TemplateService.setTemplatesFullName(x);
        return x;
      })
    );
  }

  getTemplatePreview(dto: TemplatePreviewPostDto): Observable<GeneratedTemplateGetDto> {
    return this.http.post(`${this.serviceUrl}/preview`, dto);
  }

  listGlobal(): Observable<CfsTemplate[]> {
    return this.http.get(`${this.serviceUrl}/global`).pipe(
      map((x: CfsTemplate[]) => {
        TemplateService.setTemplatesFullName(x);
        return x;
      })
    );
  }

  addTemplate(accountId: number, template: CfsTemplate): Observable<CfsTemplate> {
    return this.http.post(`${this.serviceUrl}/${accountId}`, template);
  }

  addGlobalTemplate(template: CfsTemplate): Observable<CfsTemplate> {
    return this.http.post(`${this.serviceUrl}/global`, template);
  }

  updateTemplate(template: CfsTemplate): Observable<number> {
    return this.http.put(`${this.serviceUrl}/${template.id}`, template);
  }

  updateGlobalTemplate(template: CfsTemplate): Observable<number> {
    return this.http.put(`${this.serviceUrl}/global`, template);
  }

  updateSystemTemplate(template: SystemTemplatePutDto): Observable<void> {
    return this.http.put(`${this.serviceUrl}/system`, template);
  }

  deleteTemplate(templateId: number) {
    return this.http.delete(`${this.serviceUrl}/${templateId}`);
  }

  deleteGlobalTemplate(templateId: number) {
    return this.http.delete(`${this.serviceUrl}/global/${templateId}`);
  }

  enableTemplate(templateId: number) {
    return this.http.post(`${this.serviceUrl}/${templateId}/enable`);
  }

  disableTemplate(templateId: number) {
    return this.http.post(`${this.serviceUrl}/${templateId}/disable`);
  }

  validateTemplates(): Observable<TemplateValidationResultDto[]> {
    return this.http.get(`${this.serviceUrl}/validate`);
  }

  getTemplatesStatuses(
    accountId: number,
    templates: TemplateStatusTemplatePostDto[],
    locationIds?: number[]
  ): Observable<TemplateStatusResponseDto[]> {
    const params = nonFalsyPayload({ accountId, templates, locationIds }) as TemplateStatusPostDto;

    return this.http.post(`${this.serviceUrl}/status`, params);
  }

  private static setTemplatesFullName(templates: CfsTemplate[]): CfsTemplate[] {
    templates.forEach((x) => {
      x.fullName = TemplateService.getTemplateFullName(x);
    });
    return templates;
  }

  private static getTemplateFullName(template: CfsTemplate): string {
    const fullNameIndustryPart: string = template.industryName ? ` (${template.industryName})` : "";
    return `${template.name}${fullNameIndustryPart}`;
  }
}
